import React, { useCallback, useState } from 'react';
import { Layout, Grid, ConfigProvider, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { useQuery } from 'react-query';

import AppLocale from 'lang/index';
import SideNav from 'componentsv2/layout-components/SideNav';
import TopNav from 'componentsv2/layout-components/TopNav';
import MobileNav from 'componentsv2/layout-components/MobileNav';
import HeaderNav from 'componentsv2/layout-components/HeaderNav';
import PageHeader from 'componentsv2/layout-components/PageHeader';
import Footer from 'componentsv2/layout-components/Footer';
import navigationConfig from 'configs/NavigationConfig';
import useParamsCustom from 'hooks/useParamsCustom';
import { getTournament } from 'api/events';
import { Dashboard } from 'modules/dashboard/pagesv2/dashboard';

import {
  SIDE_NAV_WIDTH,
  SIDE_NAV_COLLAPSED_WIDTH,
  NAV_TYPE_SIDE,
  NAV_TYPE_TOP,
} from 'constants/ThemeConstant';
import { TemplateUtils as utils } from 'utils/templateUtils';
import 'index.scss';
import { isNumeric } from 'utils';

const { Content } = Layout;
const { useBreakpoint } = Grid;

const AppLayout = ({ navCollapsed = true, navType = 'SIDE', location, history }) => {
  const params = useParamsCustom('/evento/:eventId');
  const [eventId, setEventId] = useState(params?.eventId);

  const eventResolver = useCallback(async (key, _eventId) => {
    const { body, status } = await getTournament(_eventId);

    return { event: body?.data?.tournament, status } || null;
  }, []);

  const { data: eventData, refetch: reloadEvent, status } = useQuery(
    ['event', eventId],
    eventId && eventResolver,
    { enabled: isNumeric(eventId) },
  );

  const urlConditions = ['grupos', 'participantes', 'estadisticas', 'informacion'];
  const validateUrl = urlConditions.some(condition => window.location.href.includes(condition));
  const currentRouteInfo = utils.getRouteInfo(navigationConfig, location.pathname);
  const screens = utils.getBreakPoint(useBreakpoint());
  const isMobile = !screens.includes('lg');
  const isNavSide = navType === NAV_TYPE_SIDE;
  const isNavTop = navType === NAV_TYPE_TOP;
  const pathHasV2 = location.pathname.includes('v2');
  const getLayoutGutter = () => {
    if (isNavTop || isMobile) {
      return 0;
    }
    return navCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH;
  };

  const currentAppLocale = AppLocale['es'];

  return (
    <IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
      <ConfigProvider locale={currentAppLocale.antd}>
        <Layout>
          <HeaderNav isMobile={isMobile} />
          {isNavTop && !isMobile ? <TopNav routeInfo={currentRouteInfo} /> : null}
          <Layout className="app-container">
            {isNavSide && !isMobile ? <SideNav routeInfo={currentRouteInfo} /> : null}
            <Layout className="app-layout" style={{ paddingLeft: getLayoutGutter() }}>
              <div className={`app-content ${isNavTop ? 'layout-top-nav' : ''}`}>
                {pathHasV2 && (
                  <div className="flex items-baseline">
                    <Button
                      className="mr-2"
                      size="large"
                      style={{ display: 'flex', alignItems: 'center' }}
                      type="link"
                      icon={<ArrowLeftOutlined />}
                      onClick={() => (validateUrl ? history.push('/dashboard') : history.goBack())}
                    >
                      Volver
                    </Button>
                    <PageHeader
                      display={true}
                      title={
                        window.location.href.includes('evento')
                          ? `Detalles del evento: ${eventData && eventData?.event?.name}`
                          : window.location.href.includes('solicitudes')
                          ? 'Solicitudes de nuevos miembros'
                          : 'Lista de Miembros'
                      }
                    />
                  </div>
                )}
                <Content>
                  <Dashboard />
                </Content>
              </div>
              <Footer />
            </Layout>
          </Layout>
          {isMobile && <MobileNav />}
        </Layout>
      </ConfigProvider>
    </IntlProvider>
  );
};

const mapStateToProps = ({ theme }) => {
  const { navCollapsed, navType, locale } = theme;
  return { navCollapsed, navType, locale };
};

export default connect(mapStateToProps)(React.memo(AppLayout));
